/**
 * Buckets transport-layer fetch failures for Sentry tags and triage.
 * String enum values match keys (team convention).
 */
export const FETCH_FAILURE_CLASSES = {
  CLIENT_FAILED_FETCH: 'CLIENT_FAILED_FETCH',
  SERVER_SOCKET_RESET: 'SERVER_SOCKET_RESET',
  SERVER_TIMEOUT: 'SERVER_TIMEOUT',
  HTTP_ERROR: 'HTTP_ERROR',
  UNKNOWN: 'UNKNOWN',
};

export const walkCauseChain = err => {
  const chain = [];
  let cur = err;
  let depth = 0;
  while (cur && depth < 6) {
    chain.push(cur);
    cur = cur.cause;
    depth += 1;
  }
  return chain;
};

/** True if one immediate retry may help (query-only callers should gate on operation type). */
export const isRetriableTransportCause = err => {
  const chain = walkCauseChain(err);
  const text = chain.map(c => `${c?.code || ''} ${c?.message || ''}`).join(' ');
  if (
    /ECONNRESET|ETIMEDOUT|EPIPE|ECONNABORTED|Failed to fetch|Load failed|Network Error/i.test(
      text
    )
  ) {
    return true;
  }
  return chain.some(c =>
    /^(ECONNRESET|ETIMEDOUT|EPIPE|ECONNABORTED)$/i.test(String(c?.code || ''))
  );
};

export const summarizeCauseChain = err =>
  walkCauseChain(err)
    .map(
      c => `${c?.name || 'Error'}: ${String(c?.message || '').slice(0, 160)}`
    )
    .join(' | ');

/**
 * @param {Error|object} err - Thrown error or Apollo networkError
 * @param {number} [httpStatus] - Response status when available (Apollo ServerError)
 */
export const classifyFetchFailure = (err, httpStatus) => {
  if (httpStatus != null && httpStatus >= 400) {
    return {
      fetchFailureClass: FETCH_FAILURE_CLASSES.HTTP_ERROR,
      causeCode: null,
      causeChainSummary: summarizeCauseChain(err),
    };
  }

  const chain = walkCauseChain(err);
  const messages = chain.map(c => String(c?.message || '')).join(' ');
  const codes = chain.map(c => c?.code).filter(Boolean);

  // Our own fetch timeout: AbortSignal.timeout() rejects with a TimeoutError
  // DOMException (no ETIMEDOUT code / message), so classify it explicitly.
  if (chain.some(c => c?.name === 'TimeoutError')) {
    return {
      fetchFailureClass: FETCH_FAILURE_CLASSES.SERVER_TIMEOUT,
      causeCode: 'FETCH_TIMEOUT',
      causeChainSummary: summarizeCauseChain(err),
    };
  }

  for (const code of codes) {
    const s = String(code);
    if (/^ECONNRESET$/i.test(s)) {
      return {
        fetchFailureClass: FETCH_FAILURE_CLASSES.SERVER_SOCKET_RESET,
        causeCode: s,
        causeChainSummary: summarizeCauseChain(err),
      };
    }
    if (/^ETIMEDOUT$/i.test(s)) {
      return {
        fetchFailureClass: FETCH_FAILURE_CLASSES.SERVER_TIMEOUT,
        causeCode: s,
        causeChainSummary: summarizeCauseChain(err),
      };
    }
  }

  if (/ECONNRESET/i.test(messages)) {
    return {
      fetchFailureClass: FETCH_FAILURE_CLASSES.SERVER_SOCKET_RESET,
      causeCode: codes[0] || null,
      causeChainSummary: summarizeCauseChain(err),
    };
  }

  if (/ETIMEDOUT|TIMEOUT/i.test(messages)) {
    return {
      fetchFailureClass: FETCH_FAILURE_CLASSES.SERVER_TIMEOUT,
      causeCode: codes[0] || null,
      causeChainSummary: summarizeCauseChain(err),
    };
  }

  const leaf = chain[chain.length - 1] || err;
  const leafMsg = String(leaf?.message || '');
  if (
    leaf?.name === 'TypeError' &&
    (/Failed to fetch/i.test(leafMsg) || /Load failed/i.test(leafMsg))
  ) {
    return {
      fetchFailureClass: FETCH_FAILURE_CLASSES.CLIENT_FAILED_FETCH,
      causeCode: null,
      causeChainSummary: summarizeCauseChain(err),
    };
  }

  if (/Network Error|ERR_NETWORK|Failed to fetch|Load failed/i.test(messages)) {
    return {
      fetchFailureClass: FETCH_FAILURE_CLASSES.CLIENT_FAILED_FETCH,
      causeCode: codes[0] || null,
      causeChainSummary: summarizeCauseChain(err),
    };
  }

  return {
    fetchFailureClass: FETCH_FAILURE_CLASSES.UNKNOWN,
    causeCode: codes[0] || null,
    causeChainSummary: summarizeCauseChain(err),
  };
};

/** Exported for Apollo onError deduplication — fetchWithRetry already reported these. */
export const FETCH_WITH_RETRY_ERROR_PREFIX = 'fetchWithRetry: Request error';
